import { useMemo, useState } from "react";
import { useListContext, useLocaleState, useTranslate } from "ra-core";
import { matchPath, useLocation } from "react-router";
import { List } from "@/components/admin/list";

import MobileHeader from "../layout/MobileHeader";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { SplitFlap } from "../misc/SplitFlap";
import { DealCardContent } from "./DealCard";
import { DealArchivedList } from "./DealArchivedList";
import { DealCreate } from "./DealCreate";
import { DealEdit } from "./DealEdit";
import { DealEmpty } from "./DealEmpty";
import { findDealLabel } from "./dealUtils";
import { getDealsByStage } from "./stages";
import type { Deal } from "../types";
import { sumReportingAmount } from "../misc/currency";

const amountColor = (stage: string) => {
  if (stage === "won") return "var(--nd-orange)";
  if (stage === "lost") return "var(--nd-accent)";
  return "var(--nd-text-display)";
};

const MobileDealsLayout = () => {
  const { dealStages, currency } = useConfigurationContext();
  const { data, isPending, error, filterValues } = useListContext<Deal>();
  const translate = useTranslate();
  const [locale = "en"] = useLocaleState();
  const location = useLocation();
  const matchCreate = matchPath("/deals/create", location.pathname);
  const matchEdit = matchPath("/deals/:id", location.pathname);

  const [activeStage, setActiveStage] = useState(
    () => dealStages[0]?.value ?? "",
  );

  const dealsByStage = useMemo(
    () => getDealsByStage(data ?? [], dealStages),
    [data, dealStages],
  );

  const activeDeals = useMemo(
    () => dealsByStage[activeStage] ?? [],
    [dealsByStage, activeStage],
  );

  const stageTotal = useMemo(
    () => sumReportingAmount(activeDeals),
    [activeDeals],
  );

  const formatTotal = (v: number) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      notation: "compact",
      maximumFractionDigits: v >= 1_000_000 ? 1 : 0,
    }).format(v);

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) {
    return (
      <main
        className="px-4 pb-24 min-h-screen"
        style={{ paddingTop: "calc(3.5rem + 2.75rem)" }}
      >
        <div
          className="flex gap-2 py-3"
          style={{ borderBottom: "1px solid var(--nd-border)" }}
        >
          {[80, 64, 72, 56].map((w, i) => (
            <div
              key={i}
              className="h-4 rounded animate-pulse shrink-0"
              style={{ width: w, background: "var(--nd-surface-raised)" }}
            />
          ))}
        </div>
        <div className="flex flex-col gap-3 pt-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 rounded-lg animate-pulse"
              style={{ background: "var(--nd-surface-raised)" }}
            />
          ))}
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main
        className="px-4 pb-24 min-h-screen flex flex-col items-center justify-center gap-3"
        style={{ paddingTop: "calc(3.5rem + 2.75rem)" }}
      >
        <p className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled">
          {translate("ra.notification.http_error", {
            _: "Failed to load deals",
          })}
        </p>
      </main>
    );
  }

  if (!data?.length && !hasFilters) {
    return (
      <DealEmpty>
        <DealArchivedList />
      </DealEmpty>
    );
  }

  return (
    <>
      {/* Stage tabs — fixed below main header */}
      <div
        className="fixed top-14 left-0 right-0 z-10"
        style={{
          background: "var(--nd-surface)",
          borderBottom: "1px solid var(--nd-border)",
        }}
      >
        <div className="flex overflow-x-auto scrollbar-none px-4">
          {dealStages.map((stage) => {
            const count = dealsByStage[stage.value]?.length ?? 0;
            const isActive = activeStage === stage.value;
            return (
              <button
                key={stage.value}
                onClick={() => setActiveStage(stage.value)}
                className="flex items-center gap-1.5 px-3 py-3 whitespace-nowrap shrink-0 relative"
                style={{
                  color: isActive
                    ? "var(--nd-text-display)"
                    : "var(--nd-text-disabled)",
                }}
              >
                <span className="font-nd-mono text-[10px] tracking-[0.08em] uppercase max-w-[120px] overflow-hidden text-ellipsis">
                  {findDealLabel(dealStages, stage.value) ?? stage.value}
                </span>
                {count > 0 && (
                  <span
                    className="font-nd-mono text-[9px] tabular-nums px-1.5 py-0.5 rounded-full leading-none"
                    style={{
                      background: isActive
                        ? amountColor(stage.value) === "var(--nd-text-display)"
                          ? "var(--nd-surface-raised)"
                          : amountColor(stage.value)
                        : "var(--nd-surface-raised)",
                      color: isActive
                        ? amountColor(stage.value) !== "var(--nd-text-display)"
                          ? "white"
                          : "var(--nd-text-secondary)"
                        : "var(--nd-text-disabled)",
                    }}
                  >
                    {count}
                  </span>
                )}
                {isActive && (
                  <span
                    className="absolute bottom-0 left-3 right-3 h-[2px] rounded-t"
                    style={{ background: amountColor(stage.value) }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main content — padded below header (56px) + tabs (44px) */}
      <main
        className="px-4 pb-24 min-h-screen overflow-y-auto"
        style={{ paddingTop: "calc(3.5rem + 2.75rem)" }}
        id="main-content"
      >
        {/* Stage summary row */}
        <div
          className="flex items-center justify-between py-2"
          style={{ borderBottom: "1px solid var(--nd-border)" }}
        >
          <span className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled">
            {activeDeals.length === 0
              ? translate("resources.deals.empty.title", {
                  _: "No deals",
                })
              : translate("resources.deals.count", {
                  smart_count: activeDeals.length,
                  _: `${activeDeals.length} deal${activeDeals.length === 1 ? "" : "s"}`,
                })}
          </span>
          {activeDeals.length > 0 && (
            <SplitFlap
              value={formatTotal(stageTotal)}
              className="font-nd-mono text-base font-medium tabular-nums leading-none"
              style={{ color: amountColor(activeStage) }}
            />
          )}
        </div>

        {/* Deal list */}
        {activeDeals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <p className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled">
              {translate("resources.deals.stage_empty", {
                _: "No deals in this stage",
              })}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 pt-4">
            {activeDeals.map((deal) => (
              <DealCardContent key={deal.id} deal={deal} />
            ))}
          </div>
        )}

        <DealArchivedList />
      </main>

      <DealCreate open={!!matchCreate} />
      <DealEdit open={!!matchEdit && !matchCreate} id={matchEdit?.params.id} />
    </>
  );
};

export const MobileDealList = () => {
  const translate = useTranslate();

  return (
    <>
      <MobileHeader>
        <span className="font-nd-mono text-[11px] tracking-[0.08em] uppercase text-nd-text-primary">
          {translate("crm.menu.deals", { _: "Pipeline" })}
        </span>
      </MobileHeader>

      <List
        perPage={100}
        filter={{ "archived_at@is": null }}
        title={false}
        sort={{ field: "index", order: "ASC" }}
        pagination={null}
      >
        <MobileDealsLayout />
      </List>
    </>
  );
};
